import { useMemo } from "react";

import { GetInventoryValueQuery } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";

import { getInventoryValueQueryDocument } from "./inventory-value.gql";

export interface UseGetInventoryValueProps {
  id: string;
  enabled?: boolean;
}

export type InventoryValueWithDataSource = GetInventoryValueQuery["inventoryValue"] | null | undefined;

export const useGetInventoryValue = ({ id, enabled = true }: UseGetInventoryValueProps) => {
  const queryClient = useQueryClient();
  const isTempId = id?.startsWith("temp-") ?? false;

  // For temp-ids, try to get from cache (optimistic updates) instead of querying backend
  // Check both individual query cache and inventory cache
  const tempIdValue = useMemo(() => {
    if (!isTempId || !id) {
      return null;
    }

    // First, try individual query cache
    const individualCache = queryClient.getQueryData(queryKeys.inventoryValues.detail(id));
    if (individualCache) {
      return individualCache;
    }

    // If not found, search in all inventory caches
    const allInventories = queryClient.getQueriesData({ queryKey: queryKeys.inventories.all });
    for (const [, inventory] of allInventories) {
      if (inventory && typeof inventory === "object" && "inventoryValueList" in inventory) {
        const inventoryValueList = (inventory as { inventoryValueList?: Array<{ id: string }> }).inventoryValueList;
        const found = inventoryValueList?.find((iv) => iv.id === id);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }, [isTempId, id, queryClient]);

  const queryResult = useQuery({
    queryKey: queryKeys.inventoryValues.detail(id),
    queryFn: async () => {
      if (!id) {
        throw new Error("Inventory Value ID is required");
      }
      const client = createOperationClient("GetInventoryValue");
      const data = await client.request(getInventoryValueQueryDocument, { input: { id } });
      return data.inventoryValue;
    },
    // Disable query for temp-ids (they don't exist in backend yet)
    enabled: enabled && !!id && !isTempId,
    staleTime: 5 * 60 * 1000, // 5 minutes for individual items
    placeholderData: keepPreviousData,
  });

  const inventoryValue = useMemo((): InventoryValueWithDataSource => {
    // For temp-ids, return cached value from optimistic updates
    if (isTempId) {
      return tempIdValue as InventoryValueWithDataSource;
    }
    return queryResult.data ?? null;
  }, [isTempId, tempIdValue, queryResult.data]);

  return {
    inventoryValue,
    loading: isTempId ? false : queryResult.isLoading,
    error: isTempId ? null : queryResult.error,
    queryResult,
  };
};
